import { styled } from "styled-components";

import {
  InfoIcon,
  PencilIcon,
  PlusIcon,
  TickIcon,
  UserGroupIcon,
} from "@vericus/cadmus-icons";
import {
  Alert,
  Avatar,
  Button,
  colors,
  Divider,
  Pill,
  Popover,
  Switch,
  Text,
  Tooltip,
} from "@vericus/cadmus-ui";

import { show } from "@ebay/nice-modal-react";

import { FinaliseGroupModal } from "@/features/group/components/finalise-group-modal";
import { useEmailConsent, useFinaliseGroup } from "@/features/group/hooks";
import { getActiveMembers, getWithdrawnMembers } from "@/features/group/utils";
import {
  AssessmentFragment,
  GroupFragment,
  MessageFragment,
  MessageRequestType,
  UserFragment,
} from "@/generated/graphql";
import { useRootNavigate } from "@/routes";
import { formatDateFull } from "@/utils/datetime";

import { useGroupChangeRequest } from "../hooks/use-group-change-request";
import { useRowFit } from "../hooks/use-row-fit";
import * as styles from "./group-card.css";
import { MEMBER_LIST_GAP } from "./group-card.css";
import * as homeStyles from "./home-page.css";

interface GroupCardProps {
  assessment: AssessmentFragment;
  finalised: boolean;
  userGroup: GroupFragment | null;
  changeRequested?: MessageFragment | null;
  workGroupId?: string;
}

export function GroupCard(props: GroupCardProps) {
  const { assessment, finalised } = props;
  const emailConsent = useEmailConsent(assessment.id);

  const settings = assessment.settings;
  const selfEnrolled = settings?.groupSelfEnrollmentEnabled;
  const deadline = settings?.groupSelfEnrollmentDeadline;

  const isDeadlinePassed = deadline ? new Date(deadline) < new Date() : false;
  const isFinalised = isDeadlinePassed || finalised;

  return (
    <div className={homeStyles.steps}>
      <div className={homeStyles.stepIcon[isFinalised ? "final" : "initial"]}>
        <UserGroupIcon label="group people" size={30} />
      </div>
      <div className={styles.groupCardRoot}>
        <div className={styles.groupCard}>
          <div>
            <Content {...props} />
            {selfEnrolled && deadline && (
              <>
                <ContentDivider />
                <div className={styles.progress}>
                  {isFinalised && !props.userGroup ? (
                    <Pill color={colors.failureBg}>Self Enrolment Closed</Pill>
                  ) : !isFinalised ? (
                    <Pill color={colors.successBg}>
                      Group Formation In Progress
                    </Pill>
                  ) : null}
                  <Text kind="bodySm">
                    <strong> Due:</strong> {formatDateFull(new Date(deadline))}
                  </Text>
                </div>
              </>
            )}
          </div>

          <div className={styles.groupCardActions}>
            {settings?.openDate && (
              <ButtonComponent
                {...props}
                selfEnrolled={selfEnrolled ?? false}
                isFinalised={isFinalised}
              />
            )}
            {emailConsent.enabled && props.userGroup && (
              <EmailConsentToggle emailConsent={emailConsent} />
            )}
          </div>
        </div>
        {props.userGroup && (
          <>
            <Divider />
            <GroupMembers userGroup={props.userGroup} />
          </>
        )}
      </div>
    </div>
  );
}

interface ButtonComponentProps extends GroupCardProps {
  selfEnrolled: boolean;
  isFinalised: boolean;
}

function ButtonComponent(props: ButtonComponentProps) {
  const sendMessage = useGroupChangeRequest(props.assessment.id);
  const rootNavigate = useRootNavigate();
  const finaliseGroup = useFinaliseGroup(props.assessment.id);

  // Handle a request to change group
  const onRequestChangeHandler = () => {
    if (!props.workGroupId) return;
    sendMessage(props.workGroupId, {
      targetUserId: null,
      messageType: MessageRequestType.GroupChange,
      expiresInDays: 3,
    });
  };
  const { userGroup, changeRequested, selfEnrolled, isFinalised } = props;

  // The "Request Group Change" button only appears when the teacher allows it,
  // and is disabled once the cutoff has passed.
  const groupChangeRequestsEnabled =
    props.assessment.settings?.groupChangeRequestsEnabled === true;
  const cutoff = props.assessment.settings?.groupChangeRequestCutoff;
  const isChangeCutoffPassed = cutoff != null && new Date(cutoff) < new Date();

  if (selfEnrolled) {
    // Deadline passed and no group — show disabled button
    if (isFinalised && !userGroup) {
      return (
        <Button
          kind="secondary"
          icon={<PlusIcon label="join a group" size={16} />}
          disabled
        >
          Join a Group
        </Button>
      );
    }

    if (!isFinalised) {
      const meetsMinLimit =
        userGroup &&
        userGroup.minLimit != null &&
        getActiveMembers(userGroup).length >= userGroup.minLimit;

      if (userGroup && meetsMinLimit) {
        return (
          <div className={styles.groupEditButton}>
            <Button
              kind="outline"
              onClick={() => rootNavigate(`/group`)}
              icon={<PencilIcon label="change group" size={24} />}
            >
              Change Group
            </Button>
            <Button
              kind="secondary"
              onClick={() =>
                show(FinaliseGroupModal, {
                  onFinalise: () => finaliseGroup(),
                  deadline:
                    props.assessment.settings?.groupSelfEnrollmentDeadline,
                })
              }
              icon={<TickIcon label="finalise group" size={24} />}
            >
              Finalise Group
            </Button>
          </div>
        );
      }

      const Icon = userGroup ? (
        <UserGroupIcon label="join a group" size={24} />
      ) : (
        <PlusIcon label="join a group" size={16} />
      );
      return (
        <Button
          kind="secondary"
          icon={Icon}
          onClick={() => rootNavigate(`/group`)}
        >
          {userGroup ? "Edit Group" : "Join a Group"}
        </Button>
      );
    }
  }

  if (userGroup && groupChangeRequestsEnabled) {
    return (
      <div className={styles.groupButton}>
        <div className={styles.groupButtonContent}>
          <Button
            kind="outline"
            onClick={onRequestChangeHandler}
            disabled={!!changeRequested || isChangeCutoffPassed}
          >
            Request Group Change
          </Button>
          <Tooltip
            content={
              isChangeCutoffPassed
                ? "The group change request submission date has passed."
                : "You can request a group change once. Your teacher will be notified."
            }
          >
            <InfoIcon label="info" size={20} />
          </Tooltip>
        </div>
      </div>
    );
  }

  return;
}

function Content(props: GroupCardProps) {
  const { userGroup, assessment, changeRequested, finalised } = props;
  const selfEnrolled = assessment.settings?.groupSelfEnrollmentEnabled;
  const deadline = assessment.settings?.groupSelfEnrollmentDeadline;
  const isDeadlinePassed = deadline ? new Date(deadline) < new Date() : false;

  let headerText: string;
  let title: string;

  if (!userGroup && selfEnrolled && (isDeadlinePassed || finalised)) {
    headerText = "You’ll be assigned to a group soon";
    title =
      "Self enrolment deadline has passed. Since you’ve not enrolled in any group yet, you’ll be assigned to a group by your teacher";
  } else if (selfEnrolled && userGroup) {
    headerText = `You have joined: ${userGroup.name}`;
    title = "You’ll collaborate with your group to submit a shared response.";
  } else if (selfEnrolled) {
    headerText = "Join a Group";
    title =
      "Join or create a group to participate in this project. If you do not select a group by the deadline, you will be automatically assigned to one.";
  } else {
    headerText = `You are a member of: ${userGroup?.name}`;
    title = userGroup ? "You were assigned to this group by your teacher." : "";
  }
  let alertText;
  let alertSeverity: "info" | "error" | "success" | undefined;

  if (changeRequested && changeRequested.expiresAt) {
    const isExpired = new Date(changeRequested.expiresAt) < new Date();
    if (changeRequested.resolvedAt) {
      alertText = "Your group has been changed by your teacher.";
      alertSeverity = "success";
    } else if (isExpired) {
      alertText = "Your group change request has auto-expired.";
      alertSeverity = "error";
    } else {
      alertText = "Your teacher has been notified.";
      alertSeverity = "info";
    }
  }

  return (
    <div className={styles.groupCardContent}>
      <Text kind="headingFour">{headerText}</Text>
      <Text kind="bodyLine">{title}</Text>
      {alertText && alertSeverity && (
        <Alert
          severity={alertSeverity}
          className={styles.groupFormationAlert}
          size="sm"
        >
          {alertText}
        </Alert>
      )}
    </div>
  );
}

interface GroupMembersProps {
  userGroup: GroupFragment;
}

// A member to display, flagged with their withdrawal status.
interface DisplayMember {
  user: UserFragment;
  withdrawn: boolean;
}

// Group members shown on a single row, sized to the container width, with any
// overflow collapsed into a popover.
// Active members are shown first, withdrawn members are appended and greyed.
function GroupMembers(props: GroupMembersProps) {
  const { userGroup } = props;

  const activeMembers = getActiveMembers(userGroup);
  const withdrawnMembers = getWithdrawnMembers(userGroup);

  const displayList: DisplayMember[] = [
    ...activeMembers.map((user) => ({ user, withdrawn: false })),
    ...withdrawnMembers.map((user) => ({ user, withdrawn: true })),
  ];

  // Any change that alters a rendered width and so the single-row fit: which
  // members show, their order, withdrawal state and names, plus the active
  // count in the label.
  const fitSignature = [
    activeMembers.length,
    ...displayList.map((m) => `${m.user.id}:${m.withdrawn ? 1 : 0}:${m.user.name}`),
  ].join("|");

  const { containerRef, measureRef, visibleCount } = useRowFit(
    displayList.length,
    MEMBER_LIST_GAP,
    fitSignature
  );

  const displayMembers = displayList.slice(0, visibleCount);
  const remainingMembers = displayList.slice(visibleCount);

  return (
    <div ref={containerRef} className={styles.memberList}>
      <Text kind="label" as="div" className={styles.memberListLabel}>
        {activeMembers.length} Members:
      </Text>
      {displayMembers.map((member) => (
        <MemberItem key={member.user.id} member={member} />
      ))}
      {remainingMembers.length > 0 && (
        <RemainingMembersPopover
          remainingMembers={remainingMembers}
          groupName={userGroup?.name}
        />
      )}
      {/* Off-screen row used only to measure intrinsic widths. The two sample
          pills must match the real overflow trigger element (a <button>, not a
          <span>) so their measured width matches — buttons don't inherit font.
          They give the pill's base and per-digit width (see useRowFit). */}
      <div ref={measureRef} className={styles.memberMeasureRow} aria-hidden>
        <Text kind="label" as="div" className={styles.memberListLabel}>
          {activeMembers.length} Members:
        </Text>
        {displayList.map((member) => (
          <MemberItem key={member.user.id} member={member} />
        ))}
        <button className={styles.remainingButton}>+8</button>
        <button className={styles.remainingButton}>+88</button>
      </div>
    </div>
  );
}

// Toggle for the student's own mutual email sharing consent, shown when the
// teacher has enabled email sharing for the assessment.
function EmailConsentToggle(props: {
  emailConsent: ReturnType<typeof useEmailConsent>;
}) {
  const { consent, setConsent } = props.emailConsent;

  return (
    <div className={styles.consentRow}>
      <Text kind="bodyMd">Share my email with group members</Text>
      <Switch
        checked={consent === true}
        onCheckedChange={(v) => setConsent(v)}
        aria-label="Share my email with group members"
      />
      <Tooltip
        content={
          consent === true
            ? "Mutual sharing — you see emails only from members who also share."
            : "Emails hidden. Turn on to share yours and see members who share theirs."
        }
      >
        <InfoIcon label="info" size={20} />
      </Tooltip>
    </div>
  );
}

function MemberItem(props: { member: DisplayMember; truncate?: boolean }) {
  const { user, withdrawn } = props.member;
  const truncate = props.truncate ?? false;
  const item = (
    <div className={styles.memberItem}>
      <Avatar name={user.name} size="md" shade={user.colourset ?? undefined} />
      <Text
        kind="bodySm"
        as="div"
        className={truncate ? styles.memberName : styles.memberNameInline}
        style={withdrawn ? { color: colors.grey400 } : undefined}
      >
        {withdrawn ? `${user.name} (Withdrawn)` : user.name}
      </Text>
    </div>
  );

  // Email is only present when the teacher enabled "Show email IDs to students"
  // AND both the viewer and this member consented to mutual sharing (the
  // backend returns null otherwise), so the tooltip shows only then.
  if (user.email == null || user.email === "") return item;

  return (
    <Tooltip content={<Text kind="bodySm">{user.email}</Text>}>{item}</Tooltip>
  );
}

function RemainingMembersPopover(props: {
  groupName: string;
  remainingMembers: DisplayMember[];
}) {
  const { remainingMembers, groupName } = props;

  return (
    <Popover.Root modal={false}>
      <Popover.Trigger asChild>
        <button className={styles.remainingButton}>
          +{remainingMembers.length}
        </button>
      </Popover.Trigger>
      <Popover.Content>
        <Popover.Card width={500} style={{ height: 500 }}>
          <div className={styles.groupNameHeader}>
            <Text kind="headingSix">{groupName}</Text>
            <Text kind="bodySm">{remainingMembers.length} members</Text>
          </div>
          <Divider />
          <div className={styles.memberGrid}>
            {remainingMembers.map((member) => (
              <MemberItem key={member.user.id} member={member} truncate />
            ))}
          </div>
        </Popover.Card>
      </Popover.Content>
    </Popover.Root>
  );
}

const ContentDivider = styled(Divider)`
  margin: 10px 0;
  width: 60%;
`;
